import React, { createContext, useCallback, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import './Notification.css';

const ICONS = {
  error: '✕',
  success: '✓',
  warning: '⚠',
  info: 'ℹ',
};

const DEFAULT_DURATION = 4000;

export const ToastContext = createContext(null);

const ToastItem = ({ toast, onRemove }) => {
  const [exiting, setExiting] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  const handleClose = () => {
    setExiting(true);
    setTimeout(() => onRemove(toast.id), 250);
  };

  React.useEffect(() => {
    if (toast.duration === 0) return;
    const timer = setTimeout(() => {
      setExiting(true);
      setTimeout(() => onRemove(toast.id), 250);
    }, toast.duration || DEFAULT_DURATION);
    return () => clearTimeout(timer);
  }, [toast, onRemove]);

  const handleCopy = async () => {
    if (!toast.copyText) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(toast.copyText);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = toast.copyText;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div
      className={`toast toast--${toast.variant} ${exiting ? 'toast--exiting' : ''}`}
      role="alert"
    >
      <span className="toast__icon">{ICONS[toast.variant]}</span>
      <span className="toast__content">{toast.message}</span>
      {toast.copyText ? (
        <button
          className={`toast__copy ${copied ? 'is-copied' : ''}`}
          onClick={handleCopy}
          aria-label="Копировать сообщение"
          title={copied ? 'Скопировано' : 'Копировать'}
        >
          ⧉
        </button>
      ) : null}
      <button className="toast__close" onClick={handleClose} aria-label="Закрыть">
        ×
      </button>
    </div>
  );
};

let idCounter = 0;

const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const toastsRef = useRef(toasts);
  toastsRef.current = toasts;

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback((message, variant = 'error', duration, options = {}) => {
    const { copyText } = options || {};
    const id = ++idCounter;
    setToasts((prev) => [...prev, { id, message, variant, duration, copyText }]);
    return id;
  }, []);

  React.useEffect(() => {
    window.__showToast = showToast;
    return () => { delete window.__showToast; };
  }, [showToast]);

  const contextValue = React.useMemo(() => ({ showToast, removeToast }), [showToast, removeToast]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {createPortal(
        <div className="toast-container">
          {toasts.map((t) => (
            <ToastItem key={t.id} toast={t} onRemove={removeToast} />
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
};

export default ToastProvider;
